<script setup lang="ts">
  import { computed, type CSSProperties } from 'vue';
  import type { CompiledFieldNode, FieldValue, SelectOption } from '@record-fill';
  import type { CheckboxProperties } from '../../../model/form.types';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const properties = computed(() => props.node.element.properties as CheckboxProperties);
  const control = computed(() => (props.node.control.kind === 'checkbox' ? props.node.control : null));
  const options = computed<SelectOption[]>(() => control.value?.options ?? []);
  const multiple = computed(() => control.value?.multiple === true);
  const isHorizontal = computed(() => properties.value.orientation === 'horizontal');
  const checked = computed<string[]>(() => (Array.isArray(props.node.value) ? props.node.value : []));

  // Same option typography rules as CheckboxElement.vue in the builder.
  const optionStyles = computed<CSSProperties>(() => {
    const p = properties.value;
    const styles: CSSProperties = { fontSize: p.optionFontSize ? `${p.optionFontSize}px` : '10px' };
    if (p.optionFontWeight === 'bold') styles.fontWeight = '700';
    else if (p.optionFontWeight === 'semibold') styles.fontWeight = '600';
    else if (p.optionFontWeight === 'normal') styles.fontWeight = '400';
    if (p.optionFontStyle === 'italic') styles.fontStyle = 'italic';
    if (p.optionTextDecoration && p.optionTextDecoration !== 'none') styles.textDecoration = p.optionTextDecoration;
    if (p.optionTextAlign) styles.textAlign = p.optionTextAlign;
    if (p.optionColor) styles.color = p.optionColor;
    return styles;
  });

  // A checkbox has no readonly attribute and a disabled one is drawn faded, so read-only keeps it
  // enabled and cancels the click that would tick it.
  function blockWhenReadOnly(event: Event): void {
    if (props.readOnly) event.preventDefault();
  }

  function toggle(option: string, isChecked: boolean): void {
    if (props.readOnly) return;
    if (!multiple.value) {
      emit('update', isChecked ? [option] : []);
      return;
    }
    const next = new Set(checked.value);
    if (isChecked) next.add(option);
    else next.delete(option);
    emit('update', options.value.map((item) => item.value).filter((value) => next.has(value)));
  }
</script>

<template>
  <div
    :class="isHorizontal ? 'flex flex-wrap gap-2' : 'flex flex-col gap-1'"
    :data-field-id="node.id"
  >
    <label
      v-for="option in options"
      :key="option.value"
      class="flex items-center gap-1.5 leading-none text-[var(--color-gray-07)]"
      :class="{ 'cursor-not-allowed opacity-60': disabled }"
      :style="optionStyles"
    >
      <input
        type="checkbox"
        class="size-3.5 shrink-0 bg-white-01 align-middle accent-[var(--theme-secondary)]"
        :checked="checked.includes(option.value)"
        :disabled="disabled"
        :tabindex="readOnly ? -1 : undefined"
        @click="blockWhenReadOnly"
        @change="toggle(option.value, ($event.target as HTMLInputElement).checked)"
      >
      <span>{{ option.label }}</span>
    </label>
  </div>
</template>
